import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import PaymentsIcon from "@mui/icons-material/Payments";
import LanguageIcon from "@mui/icons-material/Language";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SecurityIcon from "@mui/icons-material/Security";
import SchoolIcon from "@mui/icons-material/School";
import ForestIcon from "@mui/icons-material/Forest";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import Notiflix from "notiflix";
import React, { useState } from "react";
import styled from "styled-components";
import PiggyBank from "../../assets/svg/PiggyBank.svg";
import LoveEarth from "../../assets/gif/LoveEarth.gif";
import PlantOnEarth from "../../assets/gif/PlantOnEarth.gif";
import useWompiCheckout from "../../hooks/useWompiCheckout";

const MIN_DONATION_PESOS = 5000;

// Formatea pesos colombianos con separador de miles (20000 -> "20.000").
// El estado interno se mantiene como dígitos puros para no afectar la
// validación; solo cambia lo que ve el usuario en el input.
const formatPesos = (value) => {
  if (!value) return "";
  return Number(value).toLocaleString("es-CO");
};

const Donations = () => {
  const [openDonationModal, setOpenDonationModal] = useState(false);
  const [wompiAmount, setWompiAmount] = useState("");
  const [wompiAmountError, setWompiAmountError] = useState("");

  const handleOpenDonationModal = () => setOpenDonationModal(true);
  const handleCloseDonationModal = () => {
    setOpenDonationModal(false);
    setWompiAmount("");
    setWompiAmountError("");
  };

  // Hook de Wompi configurado para donación: cierra el modal padre antes de
  // abrir el widget (sino MUI bloquea el scroll) y muestra alerta de
  // agradecimiento personalizada al aprobarse el pago.
  const { openCheckout: openWompiDonation, loading: wompiLoading } = useWompiCheckout({
    closeParentModal: () => setOpenDonationModal(false),
    successAlert: {
      title: "¡Gracias por tu donación!",
      message:
        "Hemos recibido tu donación correctamente. Tu apoyo nos ayuda a seguir sirviendo a la comunidad.",
    },
  });

  const handleWompiDonate = async () => {
    const amountPesos = parseInt(String(wompiAmount).replace(/[^\d]/g, ""), 10);
    if (!Number.isFinite(amountPesos) || amountPesos < MIN_DONATION_PESOS) {
      setWompiAmountError(
        `El monto mínimo de donación es $${MIN_DONATION_PESOS.toLocaleString("es-CO")} COP.`
      );
      return;
    }
    setWompiAmountError("");

    try {
      // El backend y el widget de Wompi trabajan con centavos (amount_in_cents).
      // El usuario ingresa pesos enteros — convertimos aquí antes de enviar.
      const amountInCents = amountPesos * 100;

      await openWompiDonation({
        description: "Donación a Fenabocol",
        currency: "COP",
        invoice_origin: "Donacion",
        invoice_type: "NO_FACTURA",
        lines: [
          {
            description: "Donación voluntaria",
            product_name: "Donación",
            amount: amountInCents,
          },
        ],
      });
    } catch (e) {
      Notiflix.Notify.failure("No se pudo iniciar la donación. Intenta de nuevo.");
    }
  };

  const bankInfo = {
    bankName: "Banco de Bogotá",
    accountType: "Cuenta de ahorros",
    accountNumber: "034595330",
    accountHolder: "Federación Nacional de Bomberos de Colombia",
    documentNumber: "9000214119",
    email: "fenabocolorg@gmail.com",
    email2: "febocolorg@gmail.com",
  };

  const paymentLinks = {
    nequi: "https://www.nequi.com.co/",
    daviplata: "https://www.daviplata.com/",
    mercadoPago: "http://link.mercadopago.com.co/fenabocolorg",
    pse: "https://www.pse.com.co/persona",
  };

  const impactCards = [
    {
      icon: <FavoriteIcon sx={{ fontSize: 30 }} />,
      title: "Salvar vidas",
      description:
        "Tu aporte fortalece la capacidad de respuesta ante emergencias que afectan a familias y comunidades.",
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 30 }} />,
      title: "Mejorar equipos",
      description:
        "Ayuda a adquirir herramientas, elementos de protección y recursos clave para la atención de incidentes.",
    },
    {
      icon: <VolunteerActivismIcon sx={{ fontSize: 30 }} />,
      title: "Apoyar la misión",
      description:
        "Cada donación impulsa programas sociales, formación y acciones preventivas para servir mejor a la comunidad.",
    },
  ];

  const donationUses = [
    {
      title: "Servicios Sociales - Bomberitos",
      text: "El curso de bomberos para niños es una oportunidad única para que los más pequeños aprendan sobre la importancia de la prevención y la seguridad en caso de emergencias. A través de actividades prácticas y teóricas, los niños aprenderán los conceptos básicos de la prevención de incendios y los procedimientos de evacuación, así como también conocerán el funcionamiento y uso de los equipos y herramientas utilizados por los bomberos. Además, el curso también busca fomentar valores como la responsabilidad, el trabajo en equipo y la solidaridad. Este curso es una forma divertida y educativa para que los niños aprendan sobre la labor de los bomberos y cómo pueden contribuir a la seguridad de su comunidad.",
      image: LoveEarth,
      imageAlt: "Servicios Sociales",
      icon: <SchoolIcon sx={{ fontSize: 28 }} />,
      reverse: false,
    },
    {
      title: "Programa ambiental",
      text: "Un programa ambiental es una iniciativa diseñada para promover la conciencia y la responsabilidad ambiental, así como para implementar prácticas sostenibles en una organización, comunidad o sociedad. Este tipo de programa puede incluir actividades como la reducción de residuos, la conservación de energía y agua, la educación y concientización sobre temas ambientales, la implementación de prácticas de reciclaje, entre otros. El objetivo de un programa ambiental es reducir el impacto ambiental negativo y promover la sostenibilidad, contribuyendo así a la protección del medio ambiente y el bienestar de las personas y la biodiversidad.",
      image: PlantOnEarth,
      imageAlt: "Servicios Ambientales",
      icon: <ForestIcon sx={{ fontSize: 28 }} />,
      reverse: true,
    },
  ];

  const openLink = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, #f8f8f8 0%, #ffffff 35%, #fafafa 100%)",
        minHeight: "100vh",
        py: { xs: 4, md: 7 },
      }}
    >
      <Container maxWidth="xl">
        {/* HERO */}
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: { xs: "24px", md: "32px" },
            background:
              "linear-gradient(135deg, #fff7f7 0%, #ffffff 42%, #fff1f1 100%)",
            border: "1px solid rgba(211,47,47,0.08)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.05)",
            mb: 6,
          }}
        >
          <Grid container spacing={0}>
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                p: { xs: 3, md: 6 },
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.8,
                    borderRadius: "999px",
                    backgroundColor: "rgba(211,47,47,0.08)",
                    color: "#d32f2f",
                    mb: 2,
                  }}
                >
                  <VolunteerActivismIcon fontSize="small" />
                  <Typography sx={{ fontWeight: 700, fontSize: ".92rem" }}>
                    Dona y apoya nuestra labor
                  </Typography>
                </Box>

                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontSize: { xs: "2.7rem", md: "4.6rem" },
                    lineHeight: 1,
                    fontWeight: 700,
                    color: "#1f1f1f",
                    mb: 2,
                  }}
                >
                  Donaciones
                </Typography>

                <Typography
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.9,
                    fontSize: { xs: "1rem", md: "1.08rem" },
                    maxWidth: "720px",
                    textAlign: "justify",
                  }}
                >
                  ¡Ayúdanos a proteger a nuestra comunidad! Los bomberos están
                  en la línea de frente en emergencias que amenazan la vida y la
                  propiedad, y necesitan tu apoyo. Tu donación nos permitirá
                  adquirir equipo y tecnología de última generación, capacitar a
                  nuestro personal y fortalecer programas sociales y ambientales
                  para seguir sirviendo con compromiso y eficacia.
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ mt: 4 }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleOpenDonationModal}
                    startIcon={<FavoriteIcon />}
                    sx={{
                      borderRadius: "999px",
                      px: 3,
                      py: 1.4,
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "0 10px 25px rgba(211,47,47,0.25)",
                    }}
                  >
                    Dona aquí
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      const section = document.getElementById("donation-uses");
                      if (section) {
                        section.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                    sx={{
                      borderRadius: "999px",
                      px: 3,
                      py: 1.4,
                      fontWeight: 700,
                      textTransform: "none",
                    }}
                  >
                    Ver destino de las donaciones
                  </Button>
                </Stack>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={5}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: { xs: 3, md: 4 },
                background:
                  "radial-gradient(circle at center, rgba(211,47,47,0.08) 0%, rgba(255,255,255,0) 70%)",
              }}
            >
              <ImageDon src={PiggyBank} alt="Donaciones" />
            </Grid>
          </Grid>
        </Paper>

        {/* IMPACTO */}
        <Grid container spacing={3} sx={{ mb: 7 }}>
          {impactCards.map((item, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: 3,
                  borderRadius: "24px",
                  border: "1px solid rgba(0,0,0,0.06)",
                  backgroundColor: "#fff",
                  transition: "all .25s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 18px 36px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#d32f2f",
                    backgroundColor: "rgba(211,47,47,0.10)",
                    mb: 2,
                  }}
                >
                  {item.icon}
                </Box>

                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {item.title}
                </Typography>

                <Typography sx={{ color: "text.secondary", lineHeight: 1.8 }}>
                  {item.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* MODAL DONACIONES */}
        <Dialog
          open={openDonationModal}
          onClose={handleCloseDonationModal}
          maxWidth="sm"
          fullWidth
          sx={{
            zIndex: 10000
          }}
          PaperProps={{
            sx: {
              borderRadius: "24px",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontWeight: 700,
              pb: 1.5,
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <AccountBalanceIcon />
              <Typography variant="h5" component="div" fontWeight={700}>
                Realiza tu donación
              </Typography>
            </Box>

            <IconButton onClick={handleCloseDonationModal}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ pt: 1 }}>
            {/* DONACIÓN ONLINE — destacado arriba */}
            <Box
              sx={{
                border: "1px solid rgba(211,47,47,0.25)",
                borderRadius: "18px",
                p: 2.5,
                mb: 2,
                background:
                  "linear-gradient(135deg, #fff7f7 0%, #ffffff 60%, #fff1f1 100%)",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <CreditCardIcon sx={{ color: "#d32f2f" }} />
                <Typography variant="h6" fontWeight={700}>
                  Donar online con tarjeta o PSE
                </Typography>
              </Stack>

              <Typography variant="body2" sx={{ color: "#555", mb: 2 }}>
                Tú decides el monto. Procesamos tu donación de forma segura
                mediante Wompi.
              </Typography>

              <TextField
                fullWidth
                label="Monto a donar"
                placeholder="Ej: 20.000"
                value={formatPesos(wompiAmount)}
                onChange={(e) => {
                  // Quita el formato (puntos, comas) y guarda solo dígitos.
                  const v = e.target.value.replace(/[^\d]/g, "");
                  setWompiAmount(v);
                  if (wompiAmountError) setWompiAmountError("");
                }}
                error={Boolean(wompiAmountError)}
                helperText={
                  wompiAmountError ||
                  `Mínimo $${MIN_DONATION_PESOS.toLocaleString("es-CO")} COP`
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">COP</InputAdornment>
                  ),
                }}
                sx={{ mb: 2, backgroundColor: "#fff", borderRadius: "10px" }}
              />

              <LoadingButton
                fullWidth
                variant="contained"
                color="error"
                loading={wompiLoading}
                onClick={handleWompiDonate}
                startIcon={<FavoriteIcon />}
                sx={{
                  borderRadius: "14px",
                  py: 1.4,
                  textTransform: "none",
                  fontWeight: 700,
                  boxShadow: "0 10px 24px rgba(211,47,47,0.25)",
                }}
              >
                Donar con Wompi
              </LoadingButton>
            </Box>

            <Divider sx={{ my: 2.5 }}>
              <Typography variant="body2" color="text.secondary">
                u otros medios
              </Typography>
            </Divider>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PaymentsIcon />}
                  onClick={() => openLink(paymentLinks.mercadoPago)}
                  sx={{
                    borderRadius: "14px",
                    py: 1.3,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Mercado Pago
                </Button>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<LanguageIcon />}
                  onClick={() => openLink(paymentLinks.pse)}
                  sx={{
                    borderRadius: "14px",
                    py: 1.3,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  PSE
                </Button>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PhoneIphoneIcon />}
                  onClick={() => openLink(paymentLinks.nequi)}
                  sx={{
                    borderRadius: "14px",
                    py: 1.3,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Nequi
                </Button>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PhoneIphoneIcon />}
                  onClick={() => openLink(paymentLinks.daviplata)}
                  sx={{
                    borderRadius: "14px",
                    py: 1.3,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Daviplata
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }}>
              <Typography variant="body2" color="text.secondary">
                o por transferencia bancaria
              </Typography>
            </Divider>

            <Box
              sx={{
                border: "1px solid #E6E6E6",
                borderRadius: "18px",
                p: 2.5,
                backgroundColor: "#FAFAFA",
              }}
            >
              <Typography variant="h6" fontWeight={700} mb={2}>
                Datos bancarios
              </Typography>

              <Stack spacing={1}>
                <Typography>
                  <strong>Banco:</strong> {bankInfo.bankName}
                </Typography>
                <Typography>
                  <strong>Tipo de cuenta:</strong> {bankInfo.accountType}
                </Typography>
                <Typography>
                  <strong>Número de cuenta:</strong> {bankInfo.accountNumber}
                </Typography>
                <Typography>
                  <strong>Titular:</strong> {bankInfo.accountHolder}
                </Typography>
                <Typography>
                  <strong>NIT:</strong> {bankInfo.documentNumber}
                </Typography>
                <Typography>
                  <strong>Correo:</strong> {bankInfo.email}
                </Typography>
                <Typography>
                  <strong>Correo:</strong> {bankInfo.email2}
                </Typography>
              </Stack>
            </Box>

            <Typography
              variant="body2"
              sx={{
                mt: 3,
                color: "#666",
                lineHeight: 1.6,
              }}
            >
              Si donas por transferencia, puedes enviar el comprobante al
              correo o canal oficial de contacto para llevar el registro de la
              donación.
            </Typography>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={handleCloseDonationModal}
              sx={{
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* DESTINO DONACIONES */}
        <Box id="donation-uses" sx={{ mt: 7 }}>
          <Typography
            variant="h2"
            component="h2"
            textAlign="center"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              color: "#1f1f1f",
              fontSize: { xs: "2.2rem", md: "3.6rem" },
            }}
          >
            ¿Para qué serán las donaciones?
          </Typography>

          <Typography
            textAlign="center"
            sx={{
              maxWidth: "850px",
              mx: "auto",
              color: "text.secondary",
              lineHeight: 1.8,
              mb: 5,
            }}
          >
            Cada aporte contribuye al fortalecimiento de iniciativas sociales,
            educativas y ambientales que impactan de forma positiva a la
            comunidad.
          </Typography>

          <Stack spacing={4}>
            {donationUses.map((item, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "28px",
                  border: "1px solid rgba(0,0,0,0.06)",
                  background: "linear-gradient(180deg, #ffffff 0%, #fcfcfc 100%)",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
                }}
              >
                <Grid
                  container
                  spacing={3}
                  direction={{
                    xs: "column-reverse",
                    md: item.reverse ? "row-reverse" : "row",
                  }}
                  alignItems="center"
                >
                  <Grid item xs={12} md={7}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 54,
                        height: 54,
                        borderRadius: "16px",
                        backgroundColor: "rgba(211,47,47,0.10)",
                        color: "#d32f2f",
                        mb: 2,
                      }}
                    >
                      {item.icon}
                    </Box>

                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        fontSize: { xs: "2rem", md: "2.6rem" },
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.9,
                        textAlign: "justify",
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={5}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <GifDoc src={item.image} alt={item.imageAlt} />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Donations;

const ImageDon = styled.img`
  width: min(100%, 420px);
  pointer-events: none;
  display: block;
`;

const GifDoc = styled.img`
  width: min(100%, 280px);
  pointer-events: none;
  display: block;
`;